import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import useAuth from "./useAuth";

interface Ambassador {
  id: string;
  user_id: string;
  name: string;
  email: string;
  commission_rate: number;
  is_active: boolean;
  total_earnings: number;
  created_at: string;
}

interface ReferralCode {
  id: string;
  code: string;
  ambassador_id: string;
  is_active: boolean;
  uses_count: number;
  created_at: string;
}

interface Referral {
  id: string;
  referral_code_id: string | null;
  ambassador_id: string;
  referred_user_id: string;
  commission_amount: number;
  paid_out: boolean;
  created_at: string;
}

interface PrizeDrawEntry {
  id: string;
  buyer_id: string;
  ambassador_id: string;
  amount: number;
  entries: number;
  ambassador_share: number;
  platform_share: number;
  status: string | null;
  created_at: string | null;
}

export function useAdminAmbassadors() {
  const { user } = useAuth();
  const [ambassadors, setAmbassadors] = useState<Ambassador[]>([]);
  const [allReferrals, setAllReferrals] = useState<Referral[]>([]);
  const [allPrizeEntries, setAllPrizeEntries] = useState<PrizeDrawEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    checkAdminAndFetchData();
  }, [user]);

  const checkAdminAndFetchData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Check if user is admin
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roleData) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setIsAdmin(true);

      // Fetch all ambassadors
      const { data: ambassadorData, error: ambassadorError } = await supabase
        .from("ambassadors")
        .select("*")
        .order("created_at", { ascending: false });

      if (ambassadorError) throw ambassadorError;
      setAmbassadors((ambassadorData || []) as Ambassador[]);

      // Fetch all referrals
      const { data: referralsData, error: referralsError } = await supabase
        .from("referrals")
        .select("*")
        .order("created_at", { ascending: false });

      if (referralsError) throw referralsError;
      setAllReferrals((referralsData || []) as Referral[]);

      // Fetch all prize draw entries
      const { data: prizeData, error: prizeError } = await supabase
        .from("prize_draw_entries")
        .select("*")
        .order("created_at", { ascending: false });

      if (prizeError) throw prizeError;
      setAllPrizeEntries((prizeData || []) as PrizeDrawEntry[]);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createAmbassador = async (
    name: string,
    email: string,
    commissionRate: number = 5.0
  ): Promise<Ambassador | null> => {
    try {
      // First, check if a user with this email exists in auth
      // We'll create a placeholder user_id for now - when the ambassador signs up
      // with this email, their account will be linked
      
      // Generate a placeholder UUID for the ambassador
      // In a real scenario, you might want to invite the user first
      const placeholderUserId = crypto.randomUUID();

      const { data, error } = await supabase
        .from("ambassadors")
        .insert({
          user_id: placeholderUserId,
          name,
          email: email.toLowerCase(),
          commission_rate: commissionRate,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      setAmbassadors((prev) => [data as Ambassador, ...prev]);
      return data as Ambassador;
    } catch (error) {
      console.error("Error creating ambassador:", error);
      return null;
    }
  };

  const updateAmbassador = async (
    ambassadorId: string,
    updates: Partial<Pick<Ambassador, "is_active" | "commission_rate" | "name">>
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("ambassadors")
        .update(updates)
        .eq("id", ambassadorId);

      if (error) throw error;

      setAmbassadors((prev) =>
        prev.map((a) => (a.id === ambassadorId ? { ...a, ...updates } : a))
      );
      return true;
    } catch (error) {
      console.error("Error updating ambassador:", error);
      return false;
    }
  };

  const generateCodeForAmbassador = async (
    ambassadorId: string
  ): Promise<ReferralCode | null> => {
    try {
      // Generate code using database function
      const { data: codeResult, error: codeError } = await supabase.rpc(
        "generate_referral_code"
      );

      if (codeError) throw codeError;

      // Insert the new referral code
      const { data: newCode, error: insertError } = await supabase
        .from("referral_codes")
        .insert({
          code: codeResult,
          ambassador_id: ambassadorId,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      return newCode as ReferralCode;
    } catch (error) {
      console.error("Error generating referral code:", error);
      return null;
    }
  };

  const getAmbassadorReferralCodes = async (
    ambassadorId: string
  ): Promise<ReferralCode[]> => {
    try {
      const { data, error } = await supabase
        .from("referral_codes")
        .select("*")
        .eq("ambassador_id", ambassadorId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as ReferralCode[];
    } catch (error) {
      console.error("Error fetching referral codes:", error);
      return [];
    }
  };

  const getAmbassadorReferrals = (ambassadorId: string): Referral[] => {
    return allReferrals.filter((r) => r.ambassador_id === ambassadorId);
  };

  const getAmbassadorPrizeEntries = (ambassadorId: string): PrizeDrawEntry[] => {
    return allPrizeEntries.filter((e) => e.ambassador_id === ambassadorId);
  };

  return {
    ambassadors,
    allReferrals,
    allPrizeEntries,
    loading,
    isAdmin,
    createAmbassador,
    updateAmbassador,
    generateCodeForAmbassador,
    getAmbassadorReferralCodes,
    getAmbassadorReferrals,
    getAmbassadorPrizeEntries,
    refetch: checkAdminAndFetchData,
  };
}

export default useAdminAmbassadors;
